import React, { useEffect, useState } from 'react';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import styled from 'styled-components';
import tw from 'twin.macro';
import { useSnapshot } from 'valtio';
import { authStore } from '../../../store/authStore';
import { useStripePromise } from '../../hooks/useStripePromise';
import { accountNameOf } from '../../containers/GuestCarPage/CheckoutPage/PaymentForm';
import { Button } from '../button';
import { PaymentElementSkeleton } from '../skeletons';

const Shell = styled.div`
  ${tw`w-full my-2`}
`;

const NameLabel = styled.label`
  ${tw`block text-xs font-medium mt-3 mb-1`}
  color: var(--color-text-secondary);
`;

const NameInput = styled.input`
  ${tw`w-full rounded-md px-3 py-2 text-sm mb-3`}
`;

const Notice = styled.p`
  ${tw`text-xs mt-0 mb-3`}
  color: var(--color-text-secondary);
`;

const ErrorText = styled.p`
  ${tw`text-sm my-2`}
  color: #b91c1c;
`;

export interface CardSetupSecrets {
  clientSecret: string;
  customerSessionClientSecret?: string | null;
}

export interface CardSetupFormProps {
  /** Creates the SetupIntent on the server. Called once per mount. */
  loadSetup: () => Promise<CardSetupSecrets>;
  /** Runs once Stripe confirmed the card. Resolves when the caller is done with it. */
  onCardReady: (card: { paymentMethodId: string; setupIntentId: string }) => Promise<void>;
  submitLabel: string;
  notice?: React.ReactNode;
  busy?: boolean;
  disabled?: boolean;
  /**
   * Asked when the guest presses the button, before the card is sent to Stripe; false
   * stops there (spec 023: the site is not taking bookings yet).
   */
  beforeConfirm?: () => Promise<boolean>;
}

type FieldsProps = Pick<
  CardSetupFormProps,
  'onCardReady' | 'submitLabel' | 'notice' | 'busy' | 'disabled' | 'beforeConfirm'
>;

const CardSetupFields: React.FC<FieldsProps> = ({
  onCardReady,
  submitLabel,
  notice,
  busy = false,
  disabled = false,
  beforeConfirm,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const { user } = useSnapshot(authStore);
  const [cardholderName, setCardholderName] = useState(() => accountNameOf(user));
  const [complete, setComplete] = useState(false);
  const [ready, setReady] = useState(false);
  const [savedCardSelected, setSavedCardSelected] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (!stripe || !elements) return;
    setSaving(true);
    setError(null);
    try {
      if (beforeConfirm && !(await beforeConfirm())) return;
      const { error: setupError, setupIntent } = await stripe.confirmSetup({
        elements,
        redirect: 'if_required',
        confirmParams: {
          return_url: window.location.href,
          // FR-030: the server compares this name with the renter's.
          ...(savedCardSelected
            ? {}
            : {
                payment_method_data: {
                  billing_details: { email: user?.email ?? undefined, name: cardholderName.trim() },
                },
              }),
        },
      });
      if (setupError) {
        setError(setupError.message || 'The card could not be added.');
        return;
      }
      const paymentMethod = setupIntent?.payment_method;
      const paymentMethodId = typeof paymentMethod === 'string' ? paymentMethod : paymentMethod?.id;
      if (setupIntent?.status !== 'succeeded' || !paymentMethodId) {
        setError('The card could not be added. Please try again.');
        return;
      }
      await onCardReady({ paymentMethodId, setupIntentId: setupIntent.id });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <PaymentElement
        options={{ fields: { billingDetails: { name: 'never', email: 'never' } } }}
        onReady={() => setReady(true)}
        onChange={event => {
          setComplete(event.complete);
          setSavedCardSelected(Boolean(event.value?.payment_method));
        }}
      />
      {!ready ? <PaymentElementSkeleton /> : null}
      {ready && !savedCardSelected ? (
        <>
          <NameLabel htmlFor="setup-cardholder-name">Name on card</NameLabel>
          <NameInput
            id="setup-cardholder-name"
            autoComplete="cc-name"
            value={cardholderName}
            onChange={e => setCardholderName(e.target.value)}
            placeholder="As printed on the card"
          />
        </>
      ) : null}
      {ready && notice ? <Notice>{notice}</Notice> : null}
      {error ? <ErrorText role="alert">{error}</ErrorText> : null}
      {ready ? (
        <Button
          onClick={submit}
          text={submitLabel}
          isLoading={saving || busy}
          isDisabled={
            saving ||
            busy ||
            disabled ||
            !stripe ||
            !complete ||
            (!savedCardSelected && !cardholderName.trim())
          }
        />
      ) : null}
    </>
  );
};

/**
 * Spec 013: collects a card through a SetupIntent with the name on the card (FR-030).
 * Used for a new card on an extension (P3) and on the payment methods page (P4).
 * Remount it (change its key) after a refusal: the SetupIntent is spent.
 */
export const CardSetupForm: React.FC<CardSetupFormProps> = ({ loadSetup, ...fields }) => {
  const { stripePromise, ready } = useStripePromise();
  const [secrets, setSecrets] = useState<CardSetupSecrets | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    loadSetup()
      .then(answer => {
        if (!cancelled) setSecrets(answer);
      })
      .catch(error => {
        if (!cancelled) setLoadError(error?.message || 'The card form could not be opened.');
      });
    return () => {
      cancelled = true;
    };
    // Load once per mount; callers remount with a new key for a fresh SetupIntent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loadError) return <ErrorText role="alert">{loadError}</ErrorText>;
  if (!secrets || !ready) return <PaymentElementSkeleton />;

  return (
    <Shell>
      <Elements
        key={secrets.clientSecret}
        stripe={stripePromise}
        options={{
          clientSecret: secrets.clientSecret,
          ...(secrets.customerSessionClientSecret
            ? { customerSessionClientSecret: secrets.customerSessionClientSecret }
            : {}),
        }}
      >
        <CardSetupFields {...fields} />
      </Elements>
    </Shell>
  );
};
